import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { message, Popconfirm, Space, Table, Tooltip } from "antd";
import { areaApi } from "api/area.api";
import { notificationApi } from "api/notification.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import {
  ActionButton,
  TableLayout,
  TableLayoutFooter,
  getTableShowingRange,
} from "components/table";
import React, { useCallback, useEffect, useState } from "react";
import { Area } from "types/area";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import {
  Notification,
  NotificationMode,
  NotificationType,
} from "types/notification";
import { getTitle } from "utils";
import { formatDate, formatDateTime } from "utils/date";
import { $url } from "utils/url";
import { NotificationModal } from "./components/NotificationModal";

const { ColumnGroup, Column } = Table;

export const NotificationPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 10,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<
    Partial<Notification>
  >({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [areas, setAreas] = useState<Area[]>([]);

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
    fetchAreas();
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchAreas = async () => {
    const res = await areaApi.findAll({ page: 1, limit: 0 });
    setAreas(res.data.areas);
  };

  const fetchData = async () => {
    setLoading(true);
    const res = await notificationApi.findAll(query);
    setLoading(false);
    setData(res.data.notifications);
    setTotal(res.data.total);
  };

  const handleDelete = async (record: Notification) => {
    setLoading(true);
    await notificationApi.delete(record.id);
    message.success("Xóa thành công");
    fetchData();
    setLoading(false);
  };

  const handleSend = async (record: Notification) => {
    setLoading(true);
    try {
      await notificationApi.send(record.id);
      fetchData();
      message.success("Gửi thành công");
    } finally {
      setLoading(false);
    }
  };

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm kiếm",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedNotification({});
                  }}
                  tone="primary"
                  icon={<PlusOutlined />}
                >
                  Thêm mới
                </AppButton>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Notification>
              loading={loading}
              pagination={false}
              rowKey="id"
              dataSource={data}
            >
          <Column title="Tiêu đề" dataIndex={"title"} />
          <Column title="Nội dung" dataIndex="content" />
          {/* <Column
            title="Khu vực"
            render={(text, record: Notification) => (
              <div>
                {record.mode == NotificationMode.Global
                  ? "Tất cả"
                  : record.area?.name}
              </div>
            )}
          /> */}

          <Column
            title="Lần cuối gửi"
            render={(text, record: Notification) => (
              <div>
                <span
                  style={
                    !record.lastSentAt
                      ? { color: "red", fontWeight: "bold" }
                      : {}
                  }
                >
                  {record.lastSentAt
                    ? formatDateTime(record.lastSentAt)
                    : "Chưa gửi"}
                </span>

                <br />

                <span>Nhân viên: {record.staffSent?.name || "--"}</span>

                <br />
                <AppButton
                  size="small"
                  onClick={() => handleSend(record)}
                  tone="primary"
                >
                  Gửi thông báo
                </AppButton>
              </div>
            )}
          />

          <Column
            title="Loại"
            render={(text, record: Notification) => (
              <div>
                {record.type == NotificationType.News && (
                  <div>
                    <label htmlFor="">Tin tức</label>
                    <div>
                      <img
                        src={$url(record.news?.thumbnail)}
                        width={40}
                        alt=""
                      />
                      <br />
                      <span>{record.news?.title}</span>
                    </div>
                  </div>
                )}

                {record.type == NotificationType.Shop && (
                  <div>
                    <label htmlFor="">Doanh nghiệp</label>
                    <div>
                      <img
                        src={$url(record.shop?.ownerAvatar)}
                        width={60}
                        alt=""
                      />
                      <br />
                      <span>{record.shop?.name}</span>
                    </div>
                  </div>
                )}

                {record.type == NotificationType.Normal && (
                  <div>
                    <label htmlFor="">Thông thường</label>
                  </div>
                )}
              </div>
            )}
          />

          <Column
            title="Ngày tạo"
            render={(text, record: Notification) => (
              <span>{formatDate(record.createdAt)}</span>
            )}
          />

          <Column
            title="Thao tác"
            key="action"
            width={100}
            align="center"
            render={(text, record: Notification) => (
              <div className="flex gap-2 justify-center items-center">
                <Tooltip title="Cập nhật">
                  <ActionButton
                    aria-label="Cập nhật"
                    onClick={() => {
                      setSelectedNotification(record);
                      setVisibleModal(true);
                      setModalStatus("update");
                    }}
                  >
                    <EditOutlined style={{ color: "#6c84e5" }} />
                  </ActionButton>
                </Tooltip>

                <Tooltip title="Xóa">
                  <Popconfirm
                    onConfirm={() => handleDelete(record)}
                    placement="top"
                    title="Xác nhận xóa?"
                  >
                    <ActionButton aria-label="Xóa">
                      <DeleteOutlined style={{ color: "#ef4444" }} />
                    </ActionButton>
                  </Popconfirm>
                </Tooltip>
              </div>
            )}
          />
            </AppTable>
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                query.page = page;
                query.limit = limit;
                setQuery({ ...query });
              }}
            />
          }
        />

      <NotificationModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        notification={selectedNotification}
        status={modalStatus}
      />
    </div>
  );
};
